import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
  useExportPronunciationDictionaryMutation,
  useImportPronunciationDictionaryItemsMutation,
} from '@circuitry-ai/doc-data';
import { CtyButton, CtyShowToast, checkType } from '@circuitry-ai/doc-ui';

type DictionaryImportExportProps = Readonly<{
  dictionaryId: string;
  dictionaryName: string;
  onImported?: () => void | Promise<unknown>;
}>;

function toDictionaryDownloadFilename(name: string): string {
  const safe = name.replace(/[<>:"/\\|?*]+/g, '-').trim() || 'dictionary';
  return `${safe}-pronunciations.json`;
}

function downloadJsonFile(payload: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function mutationErrorMessage(err: unknown, fallback: string): string {
  return (
    (err as { data?: { message?: string }; message?: string })?.data?.message ||
    (err as Error)?.message ||
    fallback
  );
}

function DictionaryImportButton({
  dictionaryId,
  dictionaryName,
  onImported,
}: DictionaryImportExportProps) {
  const { t } = useTranslation();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importDictionaryItems, { isLoading: isImporting }] =
    useImportPronunciationDictionaryItemsMutation();

  const handleImportClick = useCallback(() => {
    if (!dictionaryId) {
      CtyShowToast(checkType.error, t('Dictionary id is missing.'));
      return;
    }
    importInputRef.current?.click();
  }, [dictionaryId, t]);

  const handleImportFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!dictionaryId) {
        CtyShowToast(checkType.error, t('Dictionary id is missing.'));
        return;
      }

      const isJson =
        file.type === 'application/json' || file.name.toLowerCase().endsWith('.json');
      if (!isJson) {
        CtyShowToast(checkType.error, t('Select a JSON file to import.'));
        return;
      }

      try {
        const result = await importDictionaryItems({
          dictionaryId,
          dictionaryName,
          file,
        }).unwrap();
        const added = result.summary?.added ?? 0;
        const updated = result.summary?.updated ?? 0;
        const skipped = result.summary?.skipped ?? 0;
        CtyShowToast(
          checkType.success,
          result.message ||
            t('Imported {{added}} added, {{updated}} updated, {{skipped}} skipped.', {
              added,
              updated,
              skipped,
            })
        );
        await onImported?.();
      } catch (err) {
        CtyShowToast(checkType.error, mutationErrorMessage(err, t('Failed to import dictionary.')));
      }
    },
    [dictionaryId, dictionaryName, importDictionaryItems, onImported, t]
  );

  return (
    <>
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(event) => {
          void handleImportFile(event);
        }}
      />
      <CtyButton
        size="sm"
        variant="bordered"
        isDisabled={!dictionaryId}
        isLoading={isImporting}
        onPress={handleImportClick}
        startContent={<Icon icon="mdi:upload" className="w-4 h-4" />}
      >
        {t('Import')}
      </CtyButton>
    </>
  );
}

function DictionaryExportButton({
  dictionaryId,
  dictionaryName,
}: Omit<DictionaryImportExportProps, 'onImported'>) {
  const { t } = useTranslation();
  const [exportDictionary, { isLoading: isExporting }] =
    useExportPronunciationDictionaryMutation();

  const handleExport = useCallback(async () => {
    if (!dictionaryId) {
      CtyShowToast(checkType.error, t('Dictionary id is missing.'));
      return;
    }

    try {
      const result = await exportDictionary({ dictionaryId, format: 'array' }).unwrap();
      downloadJsonFile(
        result.data ?? result,
        toDictionaryDownloadFilename(result.dictionary_name || dictionaryName)
      );
      CtyShowToast(
        checkType.success,
        result.message ||
          t('Exported {{count}} pronunciation entries.', {
            count: result.total_items ?? 0,
          })
      );
    } catch (err) {
      CtyShowToast(checkType.error, mutationErrorMessage(err, t('Failed to export dictionary.')));
    }
  }, [dictionaryId, dictionaryName, exportDictionary, t]);

  return (
    <CtyButton
      size="sm"
      variant="bordered"
      isDisabled={!dictionaryId}
      isLoading={isExporting}
      onPress={() => {
        void handleExport();
      }}
      startContent={<Icon icon="mdi:download" className="w-4 h-4" />}
    >
      {t('Export')}
    </CtyButton>
  );
}

export function DictionaryImportExport({
  dictionaryId,
  dictionaryName,
  onImported,
}: DictionaryImportExportProps) {
  return (
    <>
      <DictionaryImportButton
        dictionaryId={dictionaryId}
        dictionaryName={dictionaryName}
        onImported={onImported}
      />
      <DictionaryExportButton dictionaryId={dictionaryId} dictionaryName={dictionaryName} />
    </>
  );
}
